import SpeechInput from '../components/SpeechInput';
import VenuePhoto from '../components/VenuePhoto';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronRight, FolderOpen, MapPin, Users, Info, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ORGANIZER_ONBOARDING, organizerNeedsOnboarding } from '../config/brand';

const API = process.env.REACT_APP_BACKEND_URL;
const STAGES = { idea: 'Fikir', draft: 'Taslak', matching: 'Mekân aranıyor', poster: 'Afiş', ticketing: 'Biletleme', published: 'Yayında' };

const ProjectRow = ({ project }) => <li>
  <Link to={`/organizer/projects/${project.id}`} className="home-project-row" data-testid={`home-project-${project.id}`}>
    <span className="project-symbol"><FolderOpen size={24} strokeWidth={1.6} /></span>
    <span className="project-summary"><strong>{project.title || 'İsimsiz proje'}</strong>
      <span className="project-metadata">
        {project.preferred_districts?.length > 0 && <span><MapPin size={13} />{project.preferred_districts.join(', ')}</span>}
        {project.expected_attendees > 0 && <span><Users size={13} />{project.expected_attendees} kişi</span>}
      </span><span className="status-pill">{STAGES[project.stage] || 'Proje'}</span>
    </span><ChevronRight size={20} className="project-chevron" />
  </Link>
</li>;

const OrganizerHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const projectsOnly = params.get('view') === 'projects';
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [brief, setBrief] = useState('');
  const [projects, setProjects] = useState(null);
  const [venues, setVenues] = useState(null);
  const [profile, setProfile] = useState(null);
  const [errors, setErrors] = useState({});
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let alive = true;
    setErrors({});
    setProjects(null); setVenues(null);
    axios.get(`${API}/api/organizers/me`).then(({ data }) => {
      if (!alive) return;
      setProfile(data);
      if (organizerNeedsOnboarding(data)) navigate(ORGANIZER_ONBOARDING, { replace: true });
    }).catch(() => { if (alive) setErrors(e => ({ ...e, profile: true })); });
    axios.get(`${API}/api/organizer/event-projects`).then(({ data }) => {
      if (alive) setProjects(Array.isArray(data) ? data : []);
    }).catch(() => { if (alive) setErrors(e => ({ ...e, projects: true })); });
    axios.get(`${API}/api/venues?limit=4`).then(({ data }) => {  // A10: yalnız gösterilen 4 kart istenir
      if (alive) setVenues((Array.isArray(data) ? data : []).filter(v => v.approved && !v.hidden).slice(0, 4));
    }).catch(() => { if (alive) setErrors(e => ({ ...e, venues: true })); });
    return () => { alive = false; };
  }, [navigate, reload]);
  const firstName = user?.name?.trim().split(/\s+/)[0];
  const retry = <button type="button" className="text-action" onClick={() => setReload(n => n + 1)}><RefreshCw size={15} />Tekrar dene</button>;
  const startIdea = (event) => {
    event.preventDefault();
    if (voiceBusy) return;
    navigate('/organizer/idea', { state: { homeBrief: brief.trim(), startFresh: true } });
  };
  return <div className="organizer-home" data-testid="organizer-home">
    <div className="home-intro">
      <p className="home-greeting">{firstName ? `Merhaba ${firstName}` : 'Merhaba'}</p>
      <h1 data-testid="organizer-home-title">{projectsOnly ? 'Projelerim' : <>Bir sonraki etkinliğin<br /><span>burada başlıyor.</span></>}</h1>
      {projectsOnly && <p>Fikirlerini geliştir, detaylarını planla, kaldığın yerden devam et.</p>}
    </div>
    {errors.profile && <div className="home-notice" role="alert">Profil durumun yüklenemedi. {retry}</div>}
    {profile && !profile.approved && <div className="home-notice" role="status"><Info size={18} /><span>Profilin onay bekliyor. Meko, fikirler, mekân arama ve başvurular onaydan sonra açılır.</span><Link to="/organizer/profile">Profilini gör</Link></div>}
    {!projectsOnly && <>
      <form className="home-composer" onSubmit={startIdea}>
        <label htmlFor="home-brief">Bugün ne planlıyorsun?</label>
        <textarea id="home-brief" value={brief} onChange={e => setBrief(e.target.value)} maxLength={4000} rows={3} placeholder="Beşiktaş’ta 30 kişilik bir seramik atölyesi…" />
          <SpeechInput disabled={false} onBusyChange={setVoiceBusy} onTranscript={text => setBrief(prev => [prev.trim(), text].filter(Boolean).join(' ').slice(0, 4000))} />
        <div className="home-composer-actions">
          <button className="action-primary" type="submit" disabled={voiceBusy} data-testid="home-card-idea">Fikir oluştur <ArrowRight size={19} /></button>
          <button className="action-secondary" type="button" disabled={voiceBusy} data-testid="home-card-match" onClick={() => navigate('/organizer/search', { state: { prefill: { free_text: brief.trim() } } })}>Doğrudan mekân bul</button>
        </div>
      </form>
      <ol className="home-steps" aria-label="Etkinlik oluşturma adımları">
        {['Fikir', 'Proje', 'Mekân', 'Başvuru'].map((step, index) => <li key={step}><span className={`step-number step-${index}`}>0{index + 1}</span><strong>{step}</strong>{index < 3 && <ArrowRight size={13} aria-hidden="true" />}</li>)}
      </ol>
    </>}
    <section className="home-section" data-testid="home-projects">
      <div className="home-section-heading"><h2>{projectsOnly ? 'Tüm projelerin' : 'Kaldığın yerden devam et'}</h2>
        <Link to={projectsOnly ? '/organizer/idea' : '/organizer/home?view=projects'} className="text-action">{projectsOnly ? 'Yeni fikir' : 'Tümünü gör'}<ArrowRight size={16} /></Link>
      </div>
      <div className="home-project-list">
        {errors.projects ? <div className="home-empty" role="alert">Projelerin yüklenemedi. {retry}</div>
          : projects === null ? <p className="home-empty" role="status">Projelerin yükleniyor…</p>
          : projects.length === 0 ? <div className="home-empty" data-testid="home-projects-empty"><FolderOpen size={25} /><p>İlk etkinliğin bir fikirle başlar.</p><Link to="/organizer/idea" className="text-action">İlk fikrini oluştur <ArrowRight size={16} /></Link></div>
          : <ul>{(projectsOnly ? projects : projects.slice(0, 3)).map(project => <ProjectRow key={project.id} project={project} />)}</ul>}
      </div>
    </section>
    {!projectsOnly && <section className="home-section" aria-labelledby="venue-discovery-title">
      <div className="home-section-heading"><h2 id="venue-discovery-title">Mekânları keşfet</h2><Link to="/organizer/search" className="text-action">Mekân ara <ArrowRight size={16} /></Link></div>
      <p className="home-section-description">Mekânları tanı; etkinliğine uygunluğunu arama yaparak kontrol et.</p>
      {errors.venues ? <div className="home-empty" role="alert">Mekânlar yüklenemedi. {retry}</div>
        : venues === null ? <p className="home-empty" role="status">Mekânlar yükleniyor…</p>
        : venues.length === 0 ? <p className="home-empty">Yayınlanan mekânlar burada görünecek.</p>
        : <div className="home-venue-grid">{venues.map(venue => <article key={venue.id} className="home-venue-card">
          <Link to={`/venues/${venue.id}`} className="home-venue-photo" aria-label={`${venue.name} mekânını incele`}><VenuePhoto venue={venue} /></Link>
          <div className="home-venue-content"><h3>{venue.name}</h3><div className="venue-facts">
            <span><MapPin size={16} />{[venue.semt, venue.mahalle].filter(Boolean).join(', ') || 'Konum bilgisi eklenmemiş'}</span>
            {venue.max_capacity > 0 && <span><Users size={16} />{venue.min_capacity > 0 ? `${venue.min_capacity}–` : 'En fazla '}{venue.max_capacity} kişi</span>}
          </div><p className="venue-confirmation"><Info size={15} />Etkinlik koşullarını incele</p>
            <Link to={`/venues/${venue.id}`} className="action-secondary">Mekânı incele <ArrowRight size={17} /></Link>
          </div>
        </article>)}</div>}
    </section>}
  </div>;
};
export default OrganizerHome;
